import { createContext, useEffect, useMemo, type ReactNode } from 'react';
import type { ApiClient } from '@cef-ai/wallet-api-client';
import type { Identity } from '@cef-ai/wallet-identity';
import { AppContextStore, ApplicationsStore, DelegationsStore, browserLocalStorage } from '~/stores';
import { createIdentity } from '~/bootstrap';

export interface WalletDeps {
  identity: Identity;
  apiClient: ApiClient;
  appContextStore: AppContextStore;
  applicationsStore: ApplicationsStore;
  delegationsStore: DelegationsStore;
}

export const WalletContext = createContext<WalletDeps | null>(null);

/*
 * S1 PR2 removed `SessionShareDiag` / `SessionShareDiagContext`. The context
 * existed for exactly one thing: letting `EmbedWalletRoute` (a DESCENDANT)
 * hand its bridge's `send` up to the `BroadcastSessionShare` responder that
 * `WalletProvider` (an ANCESTOR) ran, so the responder's own diag events could
 * reach the host. With the responder deleted the context has no producer, and
 * a context whose only writer is gone is a seam waiting to be misused.
 */

export interface WalletProviderProps {
  apiBaseUrl: string;
  children?: ReactNode;
  /**
   * Test seam — override one or more dependencies. Production callers leave
   * this undefined and the provider constructs everything from scratch.
   */
  __overrides__?: Partial<WalletDeps>;
}

export function WalletProvider({ apiBaseUrl, children, __overrides__ }: WalletProviderProps) {
  const deps = useMemo<WalletDeps>(() => {
    // Only construct a real identity + api-client when at least one of the
    // two is not overridden. A test that passes both overrides should not
    // spin up a real WebAuthn ceremony adapter.
    const built = !__overrides__?.identity || !__overrides__?.apiClient ? createIdentity({ apiBaseUrl }) : null;
    const apiClient = __overrides__?.apiClient ?? built!.apiClient;
    const identity = __overrides__?.identity ?? built!.identity;
    const appContextStore = __overrides__?.appContextStore ?? new AppContextStore();
    const applicationsStore =
      __overrides__?.applicationsStore ??
      new ApplicationsStore({
        apiClient,
        // Read through closures so the store tracks the live appId and address,
        // which change after `wallet:hello` / login.
        getAppId: () => appContextStore.app.appId,
        getAddress: () => identity.addresses?.cere ?? '',
      });
    const delegationsStore =
      __overrides__?.delegationsStore ??
      new DelegationsStore({
        storage: browserLocalStorage(),
        getWalletUserAddress: () => identity.addresses?.cere ?? 'anonymous',
      });
    return { identity, apiClient, appContextStore, applicationsStore, delegationsStore };
    // We intentionally omit __overrides__ from deps — singletons stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);

  // Release identity's browser-owned handles (cross-tab BroadcastChannel +
  // subscribers) when the provider unmounts or apiBaseUrl changes and a new
  // identity replaces the old one. Without this, the prior identity's listener
  // keeps mutating its (now-orphaned) vault snapshot whenever any tab logs out,
  // and the BroadcastChannel handle stays open until GC. Idempotent.
  useEffect(() => {
    return () => {
      deps.identity.dispose();
    };
  }, [deps]);

  // S1 PR2 deleted the BroadcastSessionShare responder that lived here. Every
  // wallet-origin surface used to offer its live session to session-less
  // siblings over `BroadcastChannel('scp-wallet-v2')`, which is how a
  // registration popup got its keys into the persistent iframe. The frame now
  // re-derives its own key from PRF instead, so no surface offers a session to
  // any other and there is no channel to open.

  return <WalletContext.Provider value={deps}>{children}</WalletContext.Provider>;
}
